/**
 * useStompClient — STOMP 연결 관리 훅 (native WebSocket 기반)
 *
 * 기능:
 *   - StompWebSocketClient 생성/활성화/해제 (컴포넌트 마운트/언마운트)
 *   - ConnectionStatus 상태 추적 (connecting / connected / reconnecting / disconnected / authFailed)
 *   - /topic/console.{room} 메시지 수신 → messages state 추가
 *   - /topic/console.presence 수신 → roomCounter state 갱신
 *   - publish (SEND /app/console.send)
 *   - 토픽 방 전환 시 재구독
 *   - 재연결 시 토픽 재구독 자동 처리 (StompWebSocketClient 내부)
 *   - 인증 실패 (STOMP ERROR 프레임 WEBSOCKET-401-001) → authFailed 상태
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { StompWebSocketClient, buildStompUrl } from '../api/stompWebSocket';
import type { StompSubscription } from '../api/stompWebSocket';
import type {
  ConnectionStatus,
  ChatMessageView,
  ChatMessageRequest,
  RoomCounter,
} from '../types/realtimeConsole';

/** 최대 유지 메시지 수 (클라이언트 사이드 ring buffer) */
const MAX_MESSAGES = 200;

export interface UseStompClientResult {
  /** 현재 연결 상태 */
  status: ConnectionStatus;
  /** 수신된 메시지 목록 (최근 MAX_MESSAGES 건) */
  messages: ChatMessageView[];
  /** 현재 방 접속자/메시지 카운터 */
  roomCounter: RoomCounter | null;
  /** STOMP ERROR 코드 (인증 실패 등) */
  errorCode: string | null;
  /** 메시지 발행 */
  publish: (payload: ChatMessageRequest) => void;
  /** 수동 재연결 (deactivate → activate) */
  reconnect: () => void;
  /** 메시지 목록 초기화 */
  clearMessages: () => void;
  /** 초기 메시지 로드 (backfill 결과 주입) */
  setInitialMessages: (msgs: ChatMessageView[]) => void;
}

interface UseStompClientOptions {
  room: string;
  enabled?: boolean;
}

export function useStompClient({
  room,
  enabled = true,
}: UseStompClientOptions): UseStompClientResult {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [messages, setMessages] = useState<ChatMessageView[]>([]);
  const [roomCounter, setRoomCounter] = useState<RoomCounter | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const clientRef = useRef<StompWebSocketClient | null>(null);
  const messageSub = useRef<StompSubscription | null>(null);
  const presenceSub = useRef<StompSubscription | null>(null);
  const roomRef = useRef(room);
  roomRef.current = room;

  const addMessage = useCallback((msg: ChatMessageView) => {
    setMessages((prev) => {
      const next = [...prev, msg];
      return next.length > MAX_MESSAGES ? next.slice(next.length - MAX_MESSAGES) : next;
    });
  }, []);

  const subscribeToRoom = useCallback(
    (client: StompWebSocketClient, targetRoom: string) => {
      // 기존 구독 해제
      messageSub.current?.unsubscribe();
      presenceSub.current?.unsubscribe();

      // 메시지 토픽 구독
      messageSub.current = client.subscribe(
        `/topic/console.${targetRoom}`,
        (frame) => {
          try {
            const msg = JSON.parse(frame.body) as ChatMessageView;
            addMessage(msg);
          } catch {
            console.warn('[StompWS] Failed to parse message frame:', frame.body);
          }
        }
      );

      // presence 토픽 구독
      presenceSub.current = client.subscribe(
        '/topic/console.presence',
        (frame) => {
          try {
            const counter = JSON.parse(frame.body) as RoomCounter;
            if (counter.room === targetRoom || counter.room === roomRef.current) {
              setRoomCounter(counter);
            }
          } catch {
            console.warn('[StompWS] Failed to parse presence frame:', frame.body);
          }
        }
      );

      // 입장 — /app/console.join 구독 (backfill 1회 응답)
      // STOMP @SubscribeMapping 은 subscribe destination 으로 응답 반환
      client.subscribe('/app/console.join', (frame) => {
        try {
          const backfill = JSON.parse(frame.body) as ChatMessageView[];
          if (Array.isArray(backfill) && backfill.length > 0) {
            setMessages(backfill.slice(-MAX_MESSAGES));
          }
        } catch {
          // backfill 파싱 실패는 무시 — 빈 상태로 시작
        }
      });
    },
    [addMessage]
  );

  const createClient = useCallback(() => {
    const brokerURL = buildStompUrl('/api/eraf/ws');

    const client = new StompWebSocketClient({
      brokerURL,
      reconnectDelay: 5000,
      heartbeatOutgoing: 10000,
      heartbeatIncoming: 10000,
      onConnect: () => {
        setStatus('connected');
        setErrorCode(null);
        subscribeToRoom(client, roomRef.current);
      },
      onDisconnect: () => {
        setStatus((prev) =>
          prev === 'connected' || prev === 'reconnecting' ? 'reconnecting' : 'disconnected'
        );
      },
      onStompError: (frame) => {
        const code = frame?.headers?.['message'] ?? 'STOMP_ERROR';
        // WEBSOCKET-401-001 등 인증 실패 코드 탐지
        const isAuth =
          code.includes('401') ||
          code.includes('WEBSOCKET-401') ||
          code.includes('auth') ||
          (frame?.body ?? '').includes('WEBSOCKET-401');
        if (isAuth) {
          setStatus('authFailed');
          setErrorCode('WEBSOCKET-401-001');
        } else {
          setStatus('disconnected');
          setErrorCode(code);
        }
      },
      onWebSocketError: () => {
        setStatus((prev) => (prev === 'connecting' ? 'disconnected' : 'reconnecting'));
      },
    });

    return client;
  }, [subscribeToRoom]);

  // 초기 마운트 + enabled 변경 시 클라이언트 생성/활성화
  useEffect(() => {
    if (!enabled) {
      setStatus('disconnected');
      return;
    }

    setStatus('connecting');
    const client = createClient();
    clientRef.current = client;
    client.activate();

    return () => {
      messageSub.current?.unsubscribe();
      presenceSub.current?.unsubscribe();
      messageSub.current = null;
      presenceSub.current = null;
      client.deactivate();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // 방 변경 시 재구독
  useEffect(() => {
    const client = clientRef.current;
    if (client && client.connected) {
      subscribeToRoom(client, room);
    }
  }, [room, subscribeToRoom]);

  const publish = useCallback((payload: ChatMessageRequest) => {
    const client = clientRef.current;
    if (!client || !client.connected) {
      console.warn('[StompWS] Cannot publish: not connected');
      return;
    }
    client.publish({
      destination: '/app/console.send',
      body: JSON.stringify(payload),
    });
  }, []);

  const reconnect = useCallback(() => {
    const client = clientRef.current;
    if (client) {
      client.deactivate();
      clientRef.current = null;
    }
    messageSub.current = null;
    presenceSub.current = null;
    setStatus('connecting');
    setErrorCode(null);

    const newClient = createClient();
    clientRef.current = newClient;
    newClient.activate();
  }, [createClient]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const setInitialMessages = useCallback((msgs: ChatMessageView[]) => {
    setMessages(msgs.slice(-MAX_MESSAGES));
  }, []);

  return {
    status,
    messages,
    roomCounter,
    errorCode,
    publish,
    reconnect,
    clearMessages,
    setInitialMessages,
  };
}
